<script setup lang="ts">
  import { reactive, ref, watch } from 'vue';
  import { DatePicker, FilterModal, IconImage, SelectInput, ThemeProvider } from '@/shared/ui';
  import CalendarIcon from '@/shared/assets/icons/CalendarIcon.svg';
  import { formatDateForApi, formatDateToPtBR, parseCivilDate, useCustomToast } from '@/shared/lib';
  import { listGeneratedRecordStages, type GeneratedRecordStage } from '@/entities/GeneratedRecord';
  import type { QualityRecordStatus } from '@/entities/Document';
  import { GENERATED_RECORD_STAGES_LOAD_ERROR, QualityRecordStatusLabels } from '@/pages/home/config';
  import {
    GENERATED_RECORD_FILTER_STATUSES,
    type GeneratedRecordFilters,
  } from '../../model/generated-record-filters';
  import type { LoadLineageStages } from '../../model/useLineageStages';

  type DateField = 'createdAt' | 'filledAt';

  const DATE_FIELDS: { id: DateField; label: string }[] = [
    { id: 'createdAt', label: 'Data de Criação' },
    { id: 'filledAt', label: 'Data de Conclusão' },
  ];

  const props = withDefaults(
    defineProps<{
      prefix: string;
      currentFilters?: GeneratedRecordFilters;
      loadStages?: LoadLineageStages;
    }>(),
    { currentFilters: undefined, loadStages: listGeneratedRecordStages }
  );

  const emit = defineEmits<{
    close: [];
    'update:filter': [filters: GeneratedRecordFilters];
    'clear-filters': [];
  }>();

  const { showErrorToast } = useCustomToast();

  const draftStatus = ref<QualityRecordStatus | ''>('');
  const draftStageId = ref<string | number>('');
  const draftDates = reactive<Record<DateField, Date | null>>({ createdAt: null, filledAt: null });
  const openDate = reactive<Record<DateField, boolean>>({ createdAt: false, filledAt: false });
  const stages = ref<GeneratedRecordStage[]>([]);

  const loadFromFilters = (filters?: GeneratedRecordFilters) => {
    draftStatus.value = filters?.status ?? '';
    draftStageId.value = filters?.stageId ?? '';
    draftDates.createdAt = parseCivilDate(filters?.createdAt ?? undefined);
    draftDates.filledAt = parseCivilDate(filters?.filledAt ?? undefined);
  };

  loadFromFilters(props.currentFilters);

  watch(() => props.currentFilters, loadFromFilters, { deep: true });

  // A failure leaves the stage select empty and the panel usable: the other filters still work.
  watch(
    () => props.prefix,
    async (prefix) => {
      stages.value = [];
      try {
        const loaded = await props.loadStages(prefix);
        if (prefix === props.prefix) stages.value = loaded;
      } catch {
        if (prefix === props.prefix) {
          showErrorToast({ ...GENERATED_RECORD_STAGES_LOAD_ERROR, type: 'error' });
        }
      }
    },
    { immediate: true }
  );

  const toggleStatus = (status: QualityRecordStatus) => {
    draftStatus.value = draftStatus.value === status ? '' : status;
  };

  const toCivilDay = (date: Date | null) => (date ? (formatDateForApi(date) ?? undefined) : undefined);

  const handleApply = () => {
    emit('update:filter', {
      status: draftStatus.value || undefined,
      stageId: draftStageId.value === '' ? undefined : Number(draftStageId.value),
      createdAt: toCivilDay(draftDates.createdAt),
      filledAt: toCivilDay(draftDates.filledAt),
    });
  };

  const handleClear = () => {
    loadFromFilters();
    emit('clear-filters');
  };

  // Whether the panel remounts on the next opening depends on how the caller opens it, so closing
  // drops the discarded draft here instead of trusting a fresh mount.
  const handleClose = () => {
    loadFromFilters(props.currentFilters);
    emit('close');
  };
</script>

<template>
  <FilterModal
    content-class="w-83 max-w-[calc(100vw-2rem)]"
    @close="handleClose"
    @apply-filters="handleApply"
    @clear-filters="handleClear"
  >
    <template #content>
      <ThemeProvider>
        <div class="divide-y divide-gray-02">
          <div class="px-6 py-3">
            <h4 class="text-sm/5 font-medium text-gray-07">
              Status
            </h4>
            <div
              class="mt-1.5 flex flex-col items-start gap-1"
              data-testid="generated-record-filter-status-options"
            >
              <button
                v-for="status in GENERATED_RECORD_FILTER_STATUSES"
                :key="status"
                type="button"
                :data-testid="`generated-record-filter-status-${status}`"
                :aria-pressed="draftStatus === status"
                class="w-52 rounded-lg px-3 py-2 text-left text-sm/5"
                :class="draftStatus === status ? 'bg-(--theme-secondary) text-white' : 'text-gray-07'"
                @click="toggleStatus(status)"
              >
                {{ QualityRecordStatusLabels[status] }}
              </button>
            </div>
          </div>

          <div class="px-6 py-6">
            <h4
              id="generated-record-filter-stage-label"
              class="pb-3 text-sm/5 font-medium text-gray-07"
            >
              Etapa:
            </h4>
            <SelectInput
              :model-value="draftStageId"
              :options="stages"
              label="Selecione a etapa"
              aria-labelledby="generated-record-filter-stage-label"
              container-class="w-full"
              data-testid="generated-record-filter-stage-select"
              @update:model-value="draftStageId = $event"
            />
          </div>

          <div
            v-for="field in DATE_FIELDS"
            :key="field.id"
            class="px-6 py-6"
          >
            <h4 class="pb-3 text-sm/5 font-medium text-gray-07">
              {{ field.label }}
            </h4>
            <button
              type="button"
              :data-testid="`generated-record-filter-${field.id}-trigger`"
              class="flex h-10.5 w-45.5 items-center gap-2 rounded-lg border border-gray-02 bg-white-01 px-3 text-sm text-gray-03 focus:border-(--theme-secondary-ink) focus:outline-none"
              @click="openDate[field.id] = true"
            >
              <IconImage
                :src="CalendarIcon"
                alt="Calendário"
                class="h-5 w-5 shrink-0"
              />
              <span class="truncate">
                {{ draftDates[field.id] ? formatDateToPtBR(draftDates[field.id]) : 'Selecione uma data' }}
              </span>
            </button>
            <DatePicker
              v-model:selected-date="draftDates[field.id]"
              v-model:is-open="openDate[field.id]"
              :default-to-today="false"
              @apply="openDate[field.id] = false"
              @cancel="openDate[field.id] = false"
            />
          </div>
        </div>
      </ThemeProvider>
    </template>
  </FilterModal>
</template>
